import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EyeOff, ArrowRight } from "lucide-react";
import { useUser } from "../../context/UserContext";
import { getMyDiscoveryStatus } from "../../services/idPlus";

/** Where the full subscription card lives, and the hash that scrolls to it. */
export const DISCOVERY_CARD_HREF = "/interior-designer/phases#discovery";

/**
 * Standing prompt for a designer who is not yet discoverable on BuildVizer's own projects.
 *
 * The subscription card explaining this used to live only part-way down the Phases tab, so a
 * designer could sign up, log in and work for weeks without ever learning that the one thing
 * standing between them and BuildVizer's own buyers was a plan they had not asked for. This sits
 * above every interior-designer page until they are listed, and it is deliberately not dismissible:
 * it disappears by being acted on, not by being closed.
 *
 * Renders nothing for any other persona, on the page that already shows the full card, or once the
 * designer is discoverable.
 */
const DiscoveryPromptBanner = () => {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);

  const isDesigner = userInfo?.persona === "INTERIOR_DESIGNER";

  useEffect(() => {
    if (!isDesigner) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getMyDiscoveryStatus();
        if (!cancelled) setStatus(res);
      } catch (e) {
        // Never block the app on this — the banner is an aid, not a gate.
        if (!cancelled) setStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-check on navigation so the banner clears as soon as a request is raised elsewhere.
  }, [isDesigner, location.pathname]);

  if (!isDesigner || !status || status.isDiscoverable) return null;
  // The Phases page renders the full card, so a banner there would just repeat it.
  if (location.pathname.startsWith("/interior-designer/phases")) return null;

  const pending = status.status === "REQUESTED";

  return (
    <div className="mb-5 rounded-2xl border border-flamingo-200 bg-gradient-to-r from-flamingo-50 to-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-flamingo-100 text-flamingo-700">
            <EyeOff size={17} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              {pending
                ? "Your discovery request is with BuildVizer"
                : "Buyers on BuildVizer projects can't see your portfolio yet"}
            </p>
            <p className="mt-0.5 text-xs leading-snug text-slate-600 max-w-3xl">
              {pending
                ? "We're confirming your terms. Once it's active your portfolio starts appearing to buyers on BuildVizer's own projects."
                : status.blockedReason ||
                  "BuildVizer runs some projects itself. The discovery subscription is what puts your portfolio in front of those buyers — everything else you do is unaffected."}
            </p>
          </div>
        </div>
        {!pending && (
          <button
            type="button"
            onClick={() => navigate(DISCOVERY_CARD_HREF)}
            className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-xl bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700 transition-colors sm:self-auto"
          >
            Get listed <ArrowRight size={15} />
          </button>
        )}
      </div>
    </div>
  );
};

export default DiscoveryPromptBanner;
